<template>
    <div style="position: relative">
        <slot name="trigger" :open="openModal">
            <PhonemeSelect
                :value="ph"
                :options="options"
                :disabled="disabled"
                @selected="onPhonemeSelected"
                @cleared="onPhonemeCleared"
                @playSample="getSample"
            >
                <template #first-item>
                    <li class="select__option" @click="openModal">
                        {{ $t('editor.ssml.phoneme.enter') }}
                    </li>
                </template>
            </PhonemeSelect>
        </slot>
        <portal to="modals">
            <PhonemeModal
                v-if="showModal"
                :value="ph"
                :speaker="speaker"
                :phonemes="phonemes"
                @saved="onPhonemeSelected"
                @playSample="getSample"
                @close="showModal = false"
            />
        </portal>
        <audio ref="audio" class="phoneme-builder__audio" :src="sampleSrc" controls />
    </div>
</template>

<script>
import PhonemeSelect from './PhonemeSelect';
import PhonemeModal from './PhonemeModal';

import { mapGetters } from 'vuex';

import { searchPhonemes, savePhoneme, fetchSample } from '../../api/phonemes';

const getPh = node => {
    if (!node.attributes || !node.attributes.ph) return '';

    return node.attributes.ph;
};

export default {
    name: 'SsmlPhonemeControl',
    emits: ['change'],
    components: {
        PhonemeSelect,
        PhonemeModal
    },
    props: {
        nodes: {
            type: Array,
            default: () => []
        },
        speaker: {
            type: String,
            default: null
        },
        tag: {
            type: Object,
            default: () => ({})
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            options: [],
            ph: '',
            sampleSrc: null,
            showModal: false
        };
    },
    computed: {
        ...mapGetters(['speakerLanguage']),
        phonemes() {
            const attribute = this.tag.attribute.ph;
            const speakerLanguage = this.speakerLanguage(this.speaker);

            return attribute.refs[attribute.values[speakerLanguage]];
        },

        cleanWord() {
            if (!this.nodes[0].children) {
                return this.nodes[0].text.replace(/[!?.,:;"'`^()[\]]/g, '');
            }

            return this.nodes[0].children[0].text.replace(/[!?.,:;"'`^()[\]]/g, '');
        },
        isSingle() {
            return this.nodes.length === 1;
        }
    },
    watch: {
        nodes: {
            immediate: true,
            deep: true,
            async handler() {
                if (this.isSingle) {
                    this.ph = getPh(this.nodes[0]);
                } else {
                    this.ph = '';
                }

                await this.fetchWords();
            }
        },
        speaker: {
            async handler(value) {
                if (value) {
                    await this.fetchWords();
                }
            }
        }
    },

    methods: {
        async fetchWords() {
            const { results } = await searchPhonemes(this.cleanWord, this.speaker);

            this.options = results;
        },
        onPhonemeSelected({ phoneme, save, key }) {
            this.ph = phoneme || '';

            if (save) {
                this.savePhoneme(phoneme, key);
            }

            this.$emit('change', this.ph);
        },
        onPhonemeCleared() {
            this.$emit('change', '');
        },
        async savePhoneme(phoneme, key) {
            const phonemeExists = !!this.options.find(ph => ph.value === phoneme);
            if (phonemeExists) return;

            try {
                const speakerId = this.$store.getters.speakerIdBySlug(this.speaker);
                const response = await savePhoneme({
                    key: key || this.cleanWord,
                    value: phoneme,
                    speaker: speakerId
                });
                this.options.push(response);
            } catch (err) {
                console.log(err);
            }
        },
        async getSample(id) {
            try {
                const response = await fetchSample(id, this.speaker);
                if (response.sample) {
                    this.sampleSrc = response.sample;
                    this.$refs.audio.oncanplaythrough = () => {
                        this.$refs.audio.play();
                    };
                    this.$refs.audio.onended = () => {
                        this.sampleSrc = null;
                    };
                }
            } catch (error) {
                console.log(error);
            }
        },
        openModal() {
            this.showModal = true;
        }
    }
};
</script>

<style lang="scss" src="./SsmlPhonemeControl.scss"></style>
